import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { CreatorVerifiedIconComponent } from '../shared/components/creator-verified-icon/creator-verified-icon.component';
import { AnonymousPageLayoutComponent } from './components/anonymous-page-layout/anonymous-page-layout.component';
import { AvatarImageComponent } from './components/avatar-image/avatar-image.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { ButtonComponent } from './components/button/button.component';
import { CardComponent } from './components/card/card.component';
import { ChatItemComponent } from './components/chat-item/chat-item.component';
import { ChatComponent } from './components/chat/chat.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { CreatorProfileHeaderComponent } from './components/creator-profile-header/creator-profile-header.component';
import { CreatorProfileContentComponent } from './components/creator-profile/creator-profile-content.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { FormCardComponent } from './components/form-card/form-card.component';
import { FormElementComponent } from './components/form-element/form-element.component';
import { IconComponent } from './components/icon/icon.component';
import { InboxCardComponent } from './components/inbox-card/inbox-card.component';
import { InputPasswordComponent } from './components/input-password/input-password.component';
import { InputUploadComponent } from './components/input-upload/input-upload.component';
import { ListingDetailComponent } from './components/listing-detail/listing-detail.component';
import { ListingHeaderComponent } from './components/listing-header/listing-header.component';
import { LoadingIndicatorComponent } from './components/loading-indicator/loading-indicator.component';
import { MainNavigationItemComponent } from './components/main-navigation-item/main-navigation-item.component';
import { MainNavigationComponent } from './components/main-navigation/main-navigation.component';
import { PageContentComponent } from './components/page-content/page-content.component';
import { PageLayoutComponent } from './components/page-layout/page-layout.component';
import { PageTitleComponent } from './components/page-title/page-title.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { PrimaryCellContentComponent } from './components/primary-cell-content/primary-cell-content.component';
import { ImagesGridComponent } from './components/product-images/images-grid.component';
import { SettingsMenuComponent } from './components/profile-menu/settings-menu.component';
import { SiteHeaderComponent } from './components/site-header/site-header.component';
import { StatusIndicatorComponent } from './components/status-indicator/status-indicator.component';
import { StepCardComponent } from './components/step-card/step-card.component';
import { StepperNavigationComponent } from './components/stepper-navigation/stepper-navigation.component';
import { SwipesLogoComponent } from './components/swipes-logo/swipes-logo.component';
import { TabNavigationComponent } from './components/tab-navigation/tab-navigation.component';
import { TableCardComponent } from './components/table-card/table-card.component';
import { VideoDialogComponent } from './components/video-dialog/video-dialog.component';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { VideosGrid } from './components/videos-grid/videos-grid.component';
import { FormatDatePipe } from './i18n/format-date.pipe';
import { AsyncFormatPricePipe, FormatPricePipe } from './i18n/format-price.pipe';
import { FileDownloadDirective } from './image/file-download.directive';
import { ImagePipe } from './image/image.pipe';
import { VideoPipe } from './image/video.pipe';
import { EmailLinkLoginPage } from './pages/email-link-login-page/email-link-login-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { NotfoundPageComponent } from './pages/notfound-page/notfound-page.component';
import { OnboardingBrandPageComponent } from './pages/onboarding-brand-page/onboarding-brand-page.component';
import { OnboardingCreatorPageComponent } from './pages/onboarding-creator-page/onboarding-creator-page.component';
import { OnboardingPageComponent } from './pages/onboarding-page/onboarding-page.component';
import { TermsConditionsPageComponent } from './pages/terms-conditions-page/terms-conditions-page.component';
import { UserInvitationLoginPageComponent } from './pages/user-invitation-login-page/user-invitation-login-page.component';
import { WelcomeBrandPageComponent } from './pages/welcome-brand-page/welcome-brand-page.component';
import { WelcomeCreatorPageComponent } from './pages/welcome-creator-page/welcome-creator-page.component';
import { RouterLinkDirective } from './util/routerlink.directive';
import { TruncatePipe } from './util/truncate.pipe';

@NgModule({
  declarations: [
    CardComponent,
    ButtonComponent,
    LoginPageComponent,
    FormElementComponent,
    PageLayoutComponent,
    AnonymousPageLayoutComponent,
    PageContentComponent,
    MainNavigationComponent,
    MainNavigationItemComponent,
    NotfoundPageComponent,
    IconComponent,
    DropdownMenuComponent,
    SettingsMenuComponent,
    InputPasswordComponent,
    SiteHeaderComponent,
    TabNavigationComponent,
    ImagesGridComponent,
    ListingDetailComponent,
    LoadingIndicatorComponent,
    StatusIndicatorComponent,
    FormatPricePipe,
    AsyncFormatPricePipe,
    FormatDatePipe,
    PaginatorComponent,
    TableCardComponent,
    InputUploadComponent,
    PrimaryCellContentComponent,
    ConfirmDialogComponent,
    StepperNavigationComponent,
    ImagePipe,
    VideoPipe,
    FileDownloadDirective,
    PageTitleComponent,
    AvatarImageComponent,
    ListingHeaderComponent,
    FormCardComponent,
    UserInvitationLoginPageComponent,
    WelcomeBrandPageComponent,
    WelcomeCreatorPageComponent,
    CreatorProfileContentComponent,
    CreatorProfileHeaderComponent,
    CreatorVerifiedIconComponent,
    VideosGrid,
    VideoPlayerComponent,
    BackButtonComponent,
    TruncatePipe,
    RouterLinkDirective,
    StepCardComponent,
    ChatComponent,
    InboxCardComponent,
    ChatItemComponent,
    OnboardingPageComponent,
    OnboardingCreatorPageComponent,
    OnboardingBrandPageComponent,
    EmailLinkLoginPage,
    SwipesLogoComponent,
    VideoDialogComponent,
    TermsConditionsPageComponent,
  ],
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    RouterLinkActive,
    RouterLink,
    MatMenuModule,
    HttpClientModule,
    MatProgressSpinnerModule,
    TranslateModule,
    MatDialogModule,
    MatSnackBarModule,
  ],
  exports: [
    CardComponent,
    ButtonComponent,
    LoginPageComponent,
    PageLayoutComponent,
    PageContentComponent,
    AnonymousPageLayoutComponent,
    FormElementComponent,
    ReactiveFormsModule,
    MainNavigationComponent,
    MainNavigationItemComponent,
    RouterLinkActive,
    TranslateModule,
    IconComponent,
    DropdownMenuComponent,
    SettingsMenuComponent,
    InputPasswordComponent,
    SiteHeaderComponent,
    TabNavigationComponent,
    ListingDetailComponent,
    LoadingIndicatorComponent,
    StatusIndicatorComponent,
    FormatPricePipe,
    AsyncFormatPricePipe,
    FormsModule,
    PaginatorComponent,
    TableCardComponent,
    InputUploadComponent,
    PrimaryCellContentComponent,
    StepperNavigationComponent,
    PageTitleComponent,
    AvatarImageComponent,
    ImagePipe,
    VideoPipe,
    FileDownloadDirective,
    FormatDatePipe,
    ListingHeaderComponent,
    ImagesGridComponent,
    FormCardComponent,
    CreatorProfileContentComponent,
    CreatorProfileHeaderComponent,
    CreatorVerifiedIconComponent,
    VideosGrid,
    TruncatePipe,
    StepCardComponent,
    ChatComponent,
    InboxCardComponent,
    RouterLinkDirective,
  ],
})
export class CoreModule {}
